import { CheckCircle2, X } from 'lucide-react';
import { Button, Card, Input } from '@/design-system';
import type { MigrationLegacyClient, MigrationLegacyPhone } from '../types';
import { clampMigrationDays } from '../utils/migration-format';
import { formatPhone } from '@/design-system/utils/masks';

export interface RunMigrationPreviewItem {
  client: MigrationLegacyClient;
  phone: MigrationLegacyPhone;
}

export interface RunMigrationPanelProps {
  isSubmitting: boolean;
  onRun: () => void;
  selectedPhoneCount: number;
  selectedItems: RunMigrationPreviewItem[];
  days: number;
  onDaysChange: (days: number) => void;
  onRemovePhone: (phoneId: string) => void;
}

export function RunMigrationPanel({
  isSubmitting,
  onRun,
  selectedPhoneCount,
  selectedItems,
  days,
  onDaysChange,
  onRemovePhone,
}: RunMigrationPanelProps) {
  const hasSelection = selectedItems.length > 0;

  return (
    <Card>
      <div className="border-b border-gray-100 px-5 py-4">
        <h3 className="text-lg font-semibold text-gray-900">Prévia da migração</h3>
        <p className="text-sm text-gray-500">
          Confirme os números selecionados e a janela de histórico antes de enfileirar os jobs.
          Clique em um item para removê-lo da seleção.
        </p>
      </div>

      <div className="space-y-4 p-5">
        <div className="rounded-xl bg-gray-50 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <CheckCircle2 size={16} className="text-emerald-600" />
            Itens prontos para migração
          </div>
          <div className="mt-3 space-y-2 text-sm text-gray-600">
            <p>{selectedPhoneCount} telefone(s) selecionado(s) para a operação.</p>
            <p>
              {hasSelection
                ? 'Os jobs serão enfileirados no backend e processados em background.'
                : 'Selecione ao menos um número conectado no v2 para liberar a ação.'}
            </p>
          </div>
        </div>

        <div>
          <label htmlFor="migration-days" className="mb-1.5 block text-sm font-medium text-gray-900">
            Histórico dos últimos N dias (máx. 180)
          </label>
          <Input
            id="migration-days"
            type="number"
            min={1}
            max={180}
            value={days}
            onChange={(e) => {
              const raw = Number(e.target.value);
              onDaysChange(clampMigrationDays(Number.isNaN(raw) ? 7 : raw));
            }}
            className="max-w-[8rem]"
          />
          <p className="mt-1 text-xs text-gray-500">Padrão: 7 dias. Aceito: 1 a 180.</p>
        </div>

        {hasSelection ? (
          <div>
            <h4 className="mb-2 text-sm font-semibold text-gray-900">Seleção atual</h4>
            <div className="space-y-2">
              {selectedItems.map(({ client, phone }) => (
                <Button variant="outline" size="sm"
                  key={`${client.id}-${phone.id}`}
                  type="button"
                  onClick={() => onRemovePhone(phone.id)}
                  title="Clique para remover da seleção"
                  aria-label={`Remover ${phone.label} de ${client.name}`}
                  className="h-auto w-full justify-between whitespace-normal px-3 py-2 text-left text-sm text-gray-700 hover:border-red-200 hover:bg-red-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900">{client.name}</div>
                    <div className="text-xs text-gray-500">
                      {phone.label} · {formatPhone(phone.number)}
                    </div>
                  </div>
                  <X size={14} className="mt-0.5 shrink-0 text-gray-400" />
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
            Nenhum telefone foi selecionado ainda.
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
          <Button
            variant="primary"
            disabled={!hasSelection}
            isLoading={isSubmitting}
            onClick={onRun}
          >
            Migrar conversas
          </Button>
        </div>
      </div>
    </Card>
  );
}
